import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { CheckCircle, Warning, EnvelopeSimple, LockSimple, User as UserIcon } from "@phosphor-icons/react";
import PasswordInput from "../components/PasswordInput";

/* --------------------------------------------------------------------------
 * /accept-invite/:token
 * Public page (no auth wall): fetches invite metadata, lets the invitee sign
 * up via Supabase, then calls the public accept endpoint to link the new
 * user to the invited org.
 * -------------------------------------------------------------------------- */

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  // Fetch invite metadata
  useEffect(() => {
    let cancelled = false;
    api.get(`/public/invite/${token}`)
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch((e) => { if (!cancelled) setMetaErr(formatErr(e)); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setErr("First and last name are required.");
      return;
    }
    if ((form.password || "").length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true); setErr("");
    try {
      // 1. Create the Supabase auth user.
      const { data: signup, error: signupErr } = await supabase.auth.signUp({
        email: meta.email,
        password: form.password,
        options: { data: { first_name: form.first_name, last_name: form.last_name } },
      });
      if (signupErr) {
        // If the user already exists in Supabase, try to sign in instead.
        if (/already.*registered|exists/i.test(signupErr.message || "")) {
          const { data: signin, error: siErr } = await supabase.auth.signInWithPassword({
            email: meta.email, password: form.password,
          });
          if (siErr) throw siErr;
          if (!signin?.user?.id) throw new Error("Could not sign in with provided password.");
          signup.user = signin.user;
        } else { throw signupErr; }
      }
      const supabase_user_id = signup?.user?.id;
      if (!supabase_user_id) {
        throw new Error("Supabase did not return a user id. Please check your inbox to confirm the email if confirmation is enabled.");
      }

      // 2. Tell the backend to link this user to the inviting org.
      await api.post(`/public/invite/${token}/accept`, {
        supabase_user_id,
        first_name: form.first_name,
        last_name: form.last_name,
        name: `${form.first_name} ${form.last_name}`.trim(),
      });

      // 3. Auto-login (the AuthProvider picks up the active Supabase session).
      setOk(true);
      try { await login(meta.email, form.password); } catch { /* fall through */ }
      setTimeout(() => navigate("/", { replace: true }), 800);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  // ---------- render ----------

  if (metaErr) return <Card><Status icon={Warning} kind="error" title="Invite not found">{metaErr}</Status></Card>;
  if (!meta) return <Card><div className="p-8 text-muted-foreground text-center" data-testid="invite-loading">Loading invite…</div></Card>;
  if (!meta.active) {
    const reason = meta.inactive_reason || "inactive";
    return (
      <Card>
        <Status icon={Warning} kind="warning" title={reason === "expired" ? "Invite expired" : reason === "already_accepted" ? "Already accepted" : "Invite inactive"}>
          {reason === "expired"
            ? "This invitation link has expired. Please ask your admin to send a new one."
            : reason === "already_accepted"
              ? "This invitation has already been accepted. Please sign in below."
              : "This invitation is no longer active."}
          <div className="mt-4">
            <Link to="/login" className="btn btn-primary" data-testid="invite-goto-login">GO TO SIGN IN</Link>
          </div>
        </Status>
      </Card>
    );
  }
  if (ok) {
    return <Card><Status icon={CheckCircle} kind="success" title="Welcome aboard!">Linking you to <b>{meta.org_name}</b> and signing you in…</Status></Card>;
  }

  return (
    <Card>
      <div className="p-8" data-testid="accept-invite-page">
        <div className="mb-6">
          <div className="label-xs mb-2" data-testid="invite-type-label">
            {meta.invite_type === "contractor_org" ? "VENDOR INVITATION" :
             meta.invite_type === "customer_org" ? "CUSTOMER INVITATION" :
             "YOU\u2019VE BEEN INVITED"}
          </div>
          <h1 className="text-2xl font-semibold">
            {meta.invite_type === "contractor_org" ? "Claim" :
             meta.invite_type === "customer_org" ? "Set up" :
             "Join"}{" "}
            <span className="text-primary">{meta.org_name}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {meta.invite_type === "contractor_org"
              ? <>You&apos;ll become the admin of this contractor workspace{meta.inviting_org_name ? <> — invited by <b>{meta.inviting_org_name}</b></> : ""}.</>
              : meta.invite_type === "customer_org"
              ? <>Set up your customer account{meta.inviting_org_name ? <> with <b>{meta.inviting_org_name}</b></> : ""}.</>
              : <>{meta.inviting_org_name ? <>Set up your account with <b>{meta.inviting_org_name}</b>.</> : "Set up your account below."}</>}
          </p>
        </div>

        <Field label="EMAIL" icon={EnvelopeSimple}>
          <input className="input" value={meta.email} readOnly disabled data-testid="invite-email" />
        </Field>
        <Field label="FIRST NAME" icon={UserIcon}>
          <input
            className="input"
            value={form.first_name}
            onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            placeholder="Jane"
            data-testid="invite-first-name"
          />
        </Field>
        <Field label="LAST NAME" icon={UserIcon}>
          <input
            className="input"
            value={form.last_name}
            onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            placeholder="Doe"
            data-testid="invite-last-name"
          />
        </Field>
        <Field label="CHOOSE A PASSWORD" icon={LockSimple}>
          <PasswordInput
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="At least 8 characters"
            data-testid="invite-password"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </Field>

        {err && <div className="badge badge-red w-full justify-center mt-3" data-testid="invite-error">{err}</div>}

        <button
          onClick={submit}
          disabled={busy}
          className="btn btn-primary w-full mt-6 h-11"
          data-testid="invite-submit"
        >
          {busy ? "CREATING ACCOUNT…" : "ACCEPT INVITE & SIGN IN"}
        </button>

        <div className="text-[11px] text-muted-foreground text-center mt-4">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </div>
      </div>
    </Card>
  );
}

const Card = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-background p-4">
    <div className="surface w-full max-w-md">{children}</div>
  </div>
);

const Field = ({ label, icon: I, children }) => (
  <div className="mb-3">
    <div className="label-xs mb-1 flex items-center gap-1"><I size={11} /> {label}</div>
    {children}
  </div>
);

const Status = ({ icon: I, kind, title, children }) => {
  const tones = {
    success: "text-green-700 bg-green-50 border-green-300",
    warning: "text-amber-800 bg-amber-50 border-amber-300",
    error: "text-destructive bg-destructive/10 border-destructive/40",
  };
  return (
    <div className="p-8 text-center" data-testid={`invite-status-${kind}`}>
      <I size={36} weight="thin" className="mx-auto mb-3" />
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      <div className={`text-sm border px-3 py-2 ${tones[kind]}`}>{children}</div>
    </div>
  );
};
